'use client'

import { useState } from 'react'
import { Collapse, Text, UnstyledButton } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import { MedicationOption } from '@/types/interfaces/productDetailPage'
import { buildQuantityLabel, buildSummary, capitalize } from './medicationOptionLabels'
import classes from './MedicationOptionSelector.module.css'

interface MedicationOptionSelectorProps {
    options: MedicationOption[]
    selectedSkuId: number
    onChange: (option: MedicationOption) => void
}

interface PillOption {
    key: string
    label: string
}

/** A horizontally scrolling row of pill choices for the Form or Strength section. */
function PillRow({
    items,
    selectedKey,
    onSelect,
}: {
    items: PillOption[]
    selectedKey: string
    onSelect: (key: string) => void
}) {
    return (
        <div className={classes.pillRow}>
            {items.map((item) => {
                const isSelected = item.key === selectedKey
                return (
                    <UnstyledButton
                        key={item.key}
                        className={isSelected ? `${classes.pill} ${classes.pillSelected}` : classes.pill}
                        onClick={() => onSelect(item.key)}
                    >
                        <Text fz={16} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-primary)">
                            {item.label}
                        </Text>
                    </UnstyledButton>
                )
            })}
        </div>
    )
}

/**
 * The Form/Strength/Quantity "twister": a single disclosure that collapses to a
 * one-line summary ("Tablet · 20mg · 30 tablets · 30 day supply") and expands to
 * reveal horizontally-scrolling pill rows — first for the dose forms available on
 * this PDP, then for the strengths within the selected form — plus a locked
 * Quantity pill showing supply.display_label when present. Quantity selection
 * isn't implemented yet; when it is, that control belongs here too. A Pharmacy
 * row from the mock is intentionally omitted — pharmacy selection is a future
 * Compare-pharmacies view.
 */
export function MedicationOptionSelector({ options, selectedSkuId, onChange }: MedicationOptionSelectorProps) {
    const [isOpen, setIsOpen] = useState(false)
    const selected = options.find((option) => option.sku_id === selectedSkuId) ?? options[0]
    if (!selected) return null

    const doseForms = Array.from(new Set(options.map((option) => option.dose_form)))
    const strengthsForSelectedForm = options.filter((option) => option.dose_form === selected.dose_form)
    const quantityLabel = buildQuantityLabel(selected)

    const handleDoseFormChange = (doseForm: string) => {
        if (doseForm === selected.dose_form) return

        // Prefer keeping the same strength when the new dose form also offers it;
        // otherwise fall back to that dose form's first available strength.
        const next =
            options.find((option) => option.dose_form === doseForm && option.strength === selected.strength) ??
            options.find((option) => option.dose_form === doseForm)
        if (next) onChange(next)
    }

    const handleStrengthChange = (strength: string) => {
        if (strength === selected.strength) return

        const next = strengthsForSelectedForm.find((option) => option.strength === strength)
        if (next) onChange(next)
    }

    return (
        <div className={classes.card}>
            <UnstyledButton className={classes.headerButton} onClick={() => setIsOpen((open) => !open)}>
                {isOpen ? (
                    <Text w={80} fz={14} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-tertiary)">
                        Form
                    </Text>
                ) : (
                    <Text
                        fz={14}
                        fw="var(--gm-weight-text, 450)"
                        c="var(--gm-foreground-fg-primary)"
                        style={{ flex: 1 }}
                    >
                        {buildSummary(selected)}
                    </Text>
                )}
                <IconChevronDown
                    size={16}
                    color="var(--gm-foreground-fg-primary)"
                    className={isOpen ? classes.chevronOpen : classes.chevron}
                />
            </UnstyledButton>
            <Collapse in={isOpen}>
                <div className={classes.formPills}>
                    <PillRow
                        items={doseForms.map((doseForm) => ({ key: doseForm, label: capitalize(doseForm) }))}
                        selectedKey={selected.dose_form}
                        onSelect={handleDoseFormChange}
                    />
                </div>
                <div className={classes.section}>
                    <Text fz={14} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-tertiary)">
                        Strength
                    </Text>
                    <PillRow
                        items={strengthsForSelectedForm.map((option) => ({
                            key: option.strength,
                            label: option.strength,
                        }))}
                        selectedKey={selected.strength}
                        onSelect={handleStrengthChange}
                    />
                </div>
                {quantityLabel && (
                    <div className={classes.lastSection}>
                        <Text fz={12} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-secondary)">
                            Quantity
                        </Text>
                        <div className={classes.lockedPill}>
                            <Text fz={14} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-disabled)">
                                {quantityLabel}
                            </Text>
                        </div>
                    </div>
                )}
            </Collapse>
        </div>
    )
}
